import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ status: "pending", tries: 0 });

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setState({ status: data.payment_status, tries });
        if (data.payment_status === "paid" || data.payment_status === "failed" || data.payment_status === "expired" || tries > 30) return;
      } catch (error) {
        // Non-fatal — the poll will retry until timeout or webhook resolves
        console.warn("Payment status poll failed:", error?.message || error);
      }
      tries += 1;
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  if (!sid) return <NoSession />;

  if (state.status === "paid") {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-3xl bg-brand-green-soft text-brand-green mx-auto mb-6 flex items-center justify-center">
          <CheckCircle2 size={40} />
        </div>
        <h1 data-testid="payment-success-heading" className="text-4xl font-semibold text-brand-blue">You're Premium!</h1>
        <p className="mt-3 text-brand-muted">Thank you for supporting FuturePath. Your Premium plan is now active — unlimited AI, deeper insights, and your child's Education Twin.</p>
        <div className="mt-8 flex flex-col gap-3">
          <Link to="/dashboard" className="btn-primary">Go to my dashboard <ArrowRight size={16} /></Link>
          <Link to="/assistant" className="btn-outline">Chat with the AI Assistant</Link>
        </div>
      </div>
    );
  }
  if (state.status === "failed" || state.status === "expired") {
    return (
      <div className="max-w-lg mx-auto px-4 py-24 text-center">
        <div className="w-20 h-20 rounded-3xl bg-brand-orange-soft text-brand-orange mx-auto mb-6 flex items-center justify-center">
          <XCircle size={40} />
        </div>
        <h1 className="text-4xl font-semibold text-brand-blue">Payment {state.status}</h1>
        <p className="mt-3 text-brand-muted">No charge was made. You can try again anytime.</p>
        <Link to="/pricing" className="btn-primary mt-6">Back to pricing</Link>
      </div>
    );
  }
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <Sparkles size={32} className="text-brand-orange mx-auto mb-4" />
      <Loader2 className="animate-spin text-brand-green mx-auto mb-4" size={32} />
      <h1 className="text-3xl font-semibold text-brand-blue">Confirming your payment...</h1>
      <p className="mt-3 text-brand-muted">Hang tight — this usually takes just a couple of seconds.</p>
    </div>
  );
}

function NoSession() {
  return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-semibold text-brand-blue">No session found.</h1>
      <Link to="/pricing" className="btn-primary mt-6">Back to pricing</Link>
    </div>
  );
}
